
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { db } from "@/integrations/cloudflare/client";
import { ImportFormat, ImportStats } from "./types";

export const useImporter = (onImportComplete: () => void) => {
  const { toast } = useToast();
  const [importFormat, setImportFormat] = useState<ImportFormat>("json");
  const [importText, setImportText] = useState("");
  const [isImporting, setIsImporting] = useState(false);
  const [importStats, setImportStats] = useState<ImportStats | null>(null);

  const getPlaceholderText = () => {
    if (importFormat === "json") {
      return `[
  {
    "title": "Cookie Baking Elf",
    "description": "Your elf is helping in the kitchen by preparing some holiday cookies.",
    "required_items": ["Mixing bowl", "Rolling pin (or toy one)", "Apron", "Sprinkles container"],
    "setup_instructions": "1. Place elf next to mixing bowl\\n2. Put a tiny bit of flour on elf's hands and face\\n3. Set up rolling pin nearby\\n4. Add a sprinkle container that looks like it spilled a little",
    "difficulty": "Easy",
    "time_required": "10 minutes"
  }
]`;
    } else {
      return `title,description,required_items,setup_instructions,difficulty,time_required
Cookie Baking Elf,Your elf is helping in the kitchen by preparing some holiday cookies.,Mixing bowl;Rolling pin;Apron;Sprinkles container,1. Place elf next to mixing bowl\\n2. Put a tiny bit of flour on elf's hands and face\\n3. Set up rolling pin nearby\\n4. Add a sprinkle container that looks like it spilled a little,Easy,10 minutes
Snowball Fight,Your elf got into a snowball fight with another toy.,Toilet paper;Cotton balls;Small toy or stuffed animal,1. Create small snowballs from cotton or toilet paper\\n2. Position elf behind a 'fort' made of books\\n3. Set up another toy across from the elf with its own snowballs\\n4. Place a few snowballs in mid-air with clear tape,Medium,15 minutes`;
    }
  };

  const handleFormatChange = (value: string) => {
    setImportFormat(value as ImportFormat);
  };

  const loadExample = () => {
    setImportText(getPlaceholderText());
  };

  const handleImport = async () => {
    if (!importText.trim()) {
      toast({
        title: "No data to import",
        description: "Please enter some data to import",
        variant: "destructive",
      });
      return;
    }

    setIsImporting(true);
    setImportStats(null);

    try {
      let scenesToProcess: Record<string, any>[] = [];

      if (importFormat === "json") {
        try {
          const parsed = JSON.parse(importText);
          scenesToProcess = Array.isArray(parsed) ? parsed : [parsed];
        } catch (error) {
          toast({
            title: "Invalid JSON",
            description: "Please check your JSON format and try again",
            variant: "destructive",
          });
          setIsImporting(false);
          return;
        }
      } else if (importFormat === "csv") {
        // Simple CSV parser (assumes first row is header)
        const rows = importText.trim().split("\n");
        if (rows.length < 2) {
          toast({
            title: "Invalid CSV",
            description: "CSV must have a header row and at least one data row",
            variant: "destructive",
          });
          setIsImporting(false);
          return;
        }

        const headers = rows[0].split(",").map(h => h.trim().toLowerCase());
        
        // Check required columns
        const requiredColumns = ["title", "description", "required_items", "setup_instructions", "difficulty"];
        const missingColumns = requiredColumns.filter(col => !headers.includes(col));
        
        if (missingColumns.length > 0) {
          toast({
            title: "Missing required columns",
            description: `CSV is missing these columns: ${missingColumns.join(", ")}`,
            variant: "destructive",
          });
          setIsImporting(false);
          return;
        }

        for (let i = 1; i < rows.length; i++) {
          const values = rows[i].split(",").map(v => v.trim());
          if (values.length !== headers.length) continue;
          
          const scene: Record<string, any> = {};
          headers.forEach((header, index) => {
            if (header === "required_items") {
              // Convert semicolon-separated list to array
              scene[header] = values[index].split(";").map(s => s.trim()).filter(Boolean);
            } else {
              scene[header] = values[index];
            }
          });
          
          scenesToProcess.push(scene);
        }
      }

      // Validate each scene
      const validScenes: Array<{
        title: string;
        description: string;
        required_items: string[];
        setup_instructions: string;
        difficulty: string;
        time_required?: string | null;
      }> = [];
      
      const invalidScenes: Record<string, any>[] = [];

      for (const scene of scenesToProcess) {
        if (!scene.title || !scene.description || !scene.required_items || !scene.setup_instructions || !scene.difficulty) {
          invalidScenes.push(scene);
          continue;
        }
        
        // Ensure required_items is an array
        if (!Array.isArray(scene.required_items)) {
          scene.required_items = [scene.required_items as unknown as string];
        }
        
        // Create a valid scene object with all required fields
        const validScene = {
          title: scene.title,
          description: scene.description,
          required_items: scene.required_items as string[],
          setup_instructions: scene.setup_instructions,
          difficulty: scene.difficulty,
          time_required: scene.time_required || null
        };
        
        validScenes.push(validScene);
      }

      // Insert valid scenes into the database
      if (validScenes.length > 0) {
        const { error } = await db
          .from("elf_scenes")
          .insert(validScenes);

        if (error) throw error;
      }

      setImportStats({
        total: scenesToProcess.length,
        success: validScenes.length,
        failed: invalidScenes.length
      });

      toast({
        title: "Import complete",
        description: `Successfully imported ${validScenes.length} elf scenes. ${invalidScenes.length} failed validation.`,
        variant: invalidScenes.length > 0 ? "default" : "default",
      });

      // Reset form after successful import
      if (validScenes.length > 0) {
        setImportText("");
        onImportComplete();
      }
    } catch (error) {
      console.error("Error importing elf scenes:", error);
      toast({
        title: "Import failed",
        description: "An error occurred while importing elf scenes. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsImporting(false);
    }
  };

  return {
    importFormat,
    importText,
    isImporting,
    importStats,
    handleFormatChange,
    setImportText,
    loadExample,
    handleImport,
    getPlaceholderText
  };
};
